import React from 'react';
import { Link } from 'react-router-dom';

/** English Terms of Service — community + outdoor activity aligned. */
export default function TermsEn() {
  return (
    <>
      <h1>Terms of Service</h1>
      <p className="muted">Effective date: May 27, 2026 · Last updated: May 27, 2026</p>
      <p className="lede">
        These Terms govern your use of <strong>omw</strong> (&quot;在路上了&quot;) — the mobile app, APIs, and onmyway.city website
        (together, the &quot;Service&quot;). By creating an account or using the Service, you agree to these Terms and our{' '}
        <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not use omw.
      </p>

      <h2>1. Who operates omw</h2>
      <p>
        omw is operated by an independent solo developer (&quot;we&quot;, &quot;us&quot;). The Service is a{' '}
        <strong>community platform</strong> for discovering and organizing real-world activities — not a guide service,
        emergency service, or professional outdoor outfitter.
      </p>

      <h2>2. Eligibility</h2>
      <ul>
        <li>You must be at least <strong>13</strong> years old (or the minimum digital-consent age in your country — e.g. 16 in parts of the EEA, 14 in China).</li>
        <li>If you are under 18, you represent that a parent or guardian has reviewed these Terms with you.</li>
        <li>You may not use the Service if you are barred under applicable law or previously banned by us.</li>
      </ul>

      <h2>3. Your account</h2>
      <ul>
        <li>Keep your credentials secure. You are responsible for activity on your account.</li>
        <li>Provide accurate information. Impersonation and multiple accounts used to evade bans are prohibited.</li>
        <li>Notify <a href="mailto:safety@onmyway.city">safety@onmyway.city</a> immediately if you suspect unauthorized access.</li>
        <li>You may delete your account anytime: Profile → Settings → Account → Delete account (see Privacy Policy).</li>
      </ul>

      <h2>4. Community guidelines</h2>
      <p>
        omw is a social product. Treat others with respect — similar to community standards on Instagram, Xiaohongshu (RED), and
        other UGC platforms:
      </p>
      <ul>
        <li>Be kind. No harassment, bullying, hate speech, threats, or targeted abuse.</li>
        <li>No sexually explicit content involving minors; no non-consensual intimate imagery.</li>
        <li>No spam, fake engagement, misleading listings, or deceptive merchant offers.</li>
        <li>No doxing — do not publish others&apos; private contact information without consent.</li>
        <li>Respect intellectual property. Post only content you have rights to share.</li>
        <li>Use block and report tools when needed; we may remove content or suspend accounts that harm the community.</li>
      </ul>

      <h2>5. Location-based &amp; outdoor activity rules</h2>
      <p>
        Inspired by outdoor-ethics norms on platforms like <strong>Geocaching</strong> and <strong>AllTrails</strong>, you agree
        to:
      </p>
      <ul>
        <li><strong>Obey the law.</strong> No trespassing on private property, restricted government sites, or areas where access is prohibited.</li>
        <li><strong>Stay safe.</strong> Assess weather, terrain, wildlife, and your physical ability. Turn back when conditions are unsafe.</li>
        <li><strong>Leave no trace.</strong> Do not damage nature, cultural sites, or infrastructure. Pack out what you pack in.</li>
        <li>
          <strong>Place content responsibly.</strong> Do not bury treasures, list events, or publish routes in hazardous locations
          (cliffs, rail lines, tunnels, construction zones, mine shafts, water without safety gear, etc.).
        </li>
        <li><strong>Respect others&apos; space.</strong> Do not encourage crowds in residential areas or businesses without permission.</li>
        <li><strong>Follow local rules</strong> for parks, trails, campuses, and municipalities.</li>
      </ul>
      <p>You — not omw — choose where to go and assume the risks of real-world exploration.</p>

      <h2>6. Events, meetups &amp; meeting people</h2>
      <ul>
        <li>Meeting strangers carries inherent risk. Meet in public places, tell someone your plans, and trust your instincts.</li>
        <li>Event organizers are responsible for their listings; omw does not vet every gathering or participant.</li>
        <li>Dating-related features are optional. We do not perform background checks on users.</li>
      </ul>

      <h2>7. User content &amp; licence</h2>
      <p>
        You retain ownership of content you post. You grant omw a worldwide, non-exclusive, royalty-free licence to host, store,
        reproduce, display, and distribute that content <strong>solely to operate the Service</strong> (for example showing your
        event on the map or your message in chat). This licence ends when content is deleted, except for reasonable backup
        retention described in the Privacy Policy or where law requires retention.
      </p>
      <p>
        By posting public content, you represent that you have the necessary rights and that the content complies with these
        Terms.
      </p>

      <h2>8. Merchants &amp; verified creators</h2>
      <ul>
        <li>Business verification (Google Business Profile, email + phone OTP, or legacy application flows) indicates identity checks we perform — not an endorsement of quality or legality.</li>
        <li>Merchants must honour published coupons and comply with local consumer and advertising laws.</li>
        <li>False verification, coupon fraud, or impersonating a business is grounds for immediate termination.</li>
      </ul>

      <h2>9. Premium &amp; in-app purchases</h2>
      <ul>
        <li>Premium features are billed through Apple In-App Purchase unless otherwise stated.</li>
        <li>Refunds follow Apple&apos;s policies. Promotional access (including TestFlight trials) may be limited or withdrawn.</li>
        <li>Virtual items (for example in-app &quot;cheese&quot; balance) have no cash value and are non-transferable outside the Service except where explicitly allowed.</li>
      </ul>

      <h2>10. AI-generated content</h2>
      <p>
        AI features may produce inaccurate, offensive, or incomplete text. You are responsible for reviewing anything you publish
        or share. Do not rely on AI output for legal, medical, or safety-critical decisions.
      </p>

      <h2>11. Prohibited conduct</h2>
      <p>You may not:</p>
      <ul>
        <li>Violate law or others&apos; rights.</li>
        <li>Scrape, crawl, or reverse-engineer the Service except as law permits.</li>
        <li>Circumvent rate limits, geofences, blocks, or moderation.</li>
        <li>Use bots or scripts to manipulate leaderboards, charisma scores, or engagement metrics.</li>
        <li>Upload malware or attempt unauthorized access to our systems or other users&apos; accounts.</li>
        <li>Use SOS or emergency features for non-emergency pranks.</li>
      </ul>

      <h2>12. Enforcement, reports &amp; appeals</h2>
      <ul>
        <li>Report users or content via in-app tools or <a href="mailto:safety@onmyway.city">safety@onmyway.city</a>.</li>
        <li>We may remove content, restrict features, or terminate accounts without prior notice when we reasonably believe these Terms were violated or safety is at risk.</li>
        <li>You may appeal a moderation decision by emailing safety@onmyway.city with your username and a concise explanation.</li>
      </ul>

      <h2>13. Real-world safety disclaimer</h2>
      <p>
        Maps, routes, treasures, and events are user-generated or AI-assisted. Trails may be closed, coordinates may be
        inaccurate, and conditions change. <strong>You are solely responsible</strong> for your safety and compliance with local
        rules — the same way you would when using AllTrails, Geocaching, or a social network to plan an outing.
      </p>

      <h2>14. SOS &amp; emergency features</h2>
      <p>
        omw is <strong>not</strong> a replacement for emergency services. SOS broadcasts notify nearby users; they do not
        guarantee rescue. In an emergency, call your local emergency number first.
      </p>

      <h2>15. Third-party services</h2>
      <p>
        The Service integrates third parties listed in our Privacy Policy (maps, chat, auth, AI, etc.). Your use may also be
        subject to their terms.
      </p>

      <h2>16. Termination</h2>
      <p>
        You may stop using omw anytime. We may suspend or terminate access for violations, risk, or legal compliance. Sections
        that by nature should survive (licences granted for already-shared content, disclaimers, liability limits) will survive
        termination.
      </p>

      <h2>17. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
        INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY LAW.
      </p>

      <h2>18. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE OR REAL-WORLD ACTIVITIES CONNECTED
        TO IT. OUR AGGREGATE LIABILITY FOR ANY CLAIM IS LIMITED TO THE GREATER OF (A) AMOUNTS YOU PAID US IN THE 12 MONTHS BEFORE
        THE CLAIM OR (B) USD $50.
      </p>
      <p>
        Some jurisdictions do not allow certain exclusions; in those cases our liability is limited to the fullest extent
        permitted.
      </p>

      <h2>19. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from claims arising out of your content, your real-world activities, or your
        violation of these Terms, to the extent permitted by law.
      </p>

      <h2>20. Governing law &amp; disputes</h2>
      <p>
        These Terms are governed by the laws of <strong>New South Wales, Australia</strong>, without regard to conflict-of-law
        rules. Mandatory consumer protections in your country of residence still apply. Courts in New South Wales have
        non-exclusive jurisdiction, unless local law gives you the right to bring claims in your home courts.
      </p>

      <h2>21. Changes</h2>
      <p>
        We may update these Terms. Material changes will be announced in the app or by email when we have your address. Continued
        use after the effective date constitutes acceptance.
      </p>

      <h2>22. Contact</h2>
      <p>
        Legal / Terms: <a href="mailto:legal@onmyway.city">legal@onmyway.city</a>
        <br />
        Support: <a href="mailto:support@onmyway.city">support@onmyway.city</a>
        <br />
        Safety: <a href="mailto:safety@onmyway.city">safety@onmyway.city</a>
      </p>
    </>
  );
}
